import theme from '@components/../theme';
import { AsyncFocusBlur } from '@progress/kendo-react-common';
import {
    DateTimePicker as KendoDateTimePicker,
    DateTimePickerProps as KendoDateTimePickerProps,
} from '@progress/kendo-react-dateinputs';
import { useInternationalization } from '@progress/kendo-react-intl';
import { Popup, PopupProps, PopupCloseEvent } from '@progress/kendo-react-popup';
import { rgba } from '@utils/color.utils';
import React, { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';

export const DateTimePickerGlobalStyles = createGlobalStyle`
  .k-datetime-container {
    &&& {
      //font-size: 1rem;
      background: transparent;

      .k-datetime-wrap {
        padding: 0 !important;
      }

      .k-datetime-buttongroup {
        padding: 0.36em 0;

        .k-button-group {
          box-shadow: none;
          padding: 0 0.86em;
          gap: 0.36em;
        }

        .k-button {
          font-size: 0.86em;
          font-style: normal;
          font-weight: 500;
          line-height: 1.35em;

          width: unset !important;
          flex: 0 0 auto;
          padding: 0.86em 1.14em;
          color: ${theme.colors.road_grey};

          &:before {
            display: none;
          }

          &.k-active,
          &:hover {
            color: ${theme.colors.road_grey} !important;
            box-shadow: inset 0px -2px 0px ${theme.colors.brand_primary_blue_dark};
            background: white;
          }
        }
      }

      .k-datetime-selector {
        .k-datetime-calendar-wrap {
          .k-calendar-view {
            height: 20.7em;

            &:after {
              display: none;
            }
          }

          .k-calendar-header {
            .k-button {
              font-style: normal;
              font-weight: 500;
              font-size: 1.14em;
              line-height: 1.35em;
              letter-spacing: -0.01em;
              color: ${theme.colors.base_grey};
              margin-left: -0.42em;
            }

            .k-today {
              font-style: normal;
              font-weight: 500;
              font-size: 0.86em;
              line-height: 1em;
              color: ${theme.colors.poppy_red};
            }
          }

          .k-calendar-weekdays {
            .k-calendar-th {
              background: transparent !important;
              font-style: normal;
              font-weight: 500;
              font-size: 0.86em;
              line-height: 1.35em;
              color: ${theme.colors.base_grey};
              opacity: 1 !important;
              text-transform: none;
              padding-left: 0.71em;

              &:first-child {
                padding-left: 0;
              }
            }
          }

          .k-content {
            .k-calendar-table {
              .k-calendar-td {
                font-weight: normal;
                font-size: 0.86em;
                line-height: 1.071em;

                width: 3.2em;
                height: 3.2em;
                padding-left: 0.2em;
                padding-top: 0.2em;

                &:first-child {
                  padding-left: 0;
                }

                .k-link {
                  width: 2.67em;
                  height: 2.67em;
                }

                &.k-selected {
                  .k-link {
                    background: ${theme.colors.poppy_red};
                    box-shadow: none;
                  }
                }
              }
            }
          }
        }

        .k-datetime-time-wrap {
          .k-time-header {
            padding-bottom: 1em;

            .k-title {
              font-style: normal;
              font-weight: 500;
              font-size: 1.14em;
              line-height: 1.35em;
              letter-spacing: -0.01em;
              color: ${theme.colors.base_grey};
            }

            .k-time-now {
              font-style: normal;
              font-weight: 500;
              font-size: 0.86em;
              line-height: 1em;
              text-transform: uppercase;
              color: ${theme.colors.poppy_red};
            }
          }

          .k-time-list-container {
            .k-time-list-wrapper {
              .k-title {
                font-style: normal;
                font-weight: 500;
                font-size: 0.86em;
                line-height: 1.35em;
                color: ${theme.colors.road_grey_600};
                opacity: 1;
              }

              &.k-focus {
                box-shadow: none;

                &:before {
                  background: linear-gradient(180deg, rgba(245, 246, 248, 0) 0%, #F5F6F8 100%);
                }

                &:after {
                  background: linear-gradient(180deg, #F5F6F8 0%, rgba(245, 246, 248, 0) 100%);
                }

                .k-title {
                  color: ${theme.colors.base_grey};
                }
              }

              .k-time-container {
                width: calc(100% + 0.572em) !important;

                .k-item {
                  line-height: 2.715em;

                  &:hover {
                    background: transparent;
                  }
                }
              }
            }

            .k-time-separator {
              display: inline-flex;
              color: ${theme.colors.road_grey_900};
            }
          }
        }

        .k-datetime-footer {
          border-top: solid 1px ${theme.colors.road_grey_100};
          padding: 0.86em 1.43em;
          justify-content: flex-end;

          .k-button {
            font-style: normal;
            font-weight: 500;
            font-size: 0.86em;
            line-height: 1em;
            padding: 0.86em 1.14em;
            border-radius: 0.28em;
            color: ${theme.colors.brand_primary_blue_dark};
            background: white;
            border: solid 1px ${theme.colors.road_grey_400};
            flex: 0 0 auto;
            cursor: pointer !important;

            &.k-disabled {
              opacity: 0.8;
            }

            &:before {
              display: none;
            }

            &:hover {
              background: ${rgba(theme.colors.snow_white, 0.8)} !important;
            }

            &.k-primary {
              background: ${theme.colors.brand_primary_blue};
              color: white;
              border: none;

              &:hover {
                background: #4EA8F4 !important;
              }
            }
          }
        }
      }
    }
  }
`;

const Styles = styled.div.attrs((props) => ({ className: 'app-date-time-picker' }))`
    &&&.app-date-time-picker {
        //font-size: 1rem;
        display: flex;
        flex-direction: column;

        .trigger-wrap {
            display: flex;
            flex-direction: column;
        }

        .trigger {
            line-height: 2.29em;
            cursor: pointer;
            padding: 0 0.36em;
            border-bottom: solid 1px transparent;

            &.trigger-open {
                background: white;
                border-bottom-color: ${theme.colors.brand_primary_blue};
            }

            .trigger-text {
                display: flex;
                align-items: baseline;
                gap: 0.36em;
                color: ${theme.colors.base_grey};
                font-style: normal;
                font-size: 0.86em;

                .part-date {
                    font-weight: 500;
                }

                .part-text {
                    font-weight: 400;
                }
            }

            .trigger-placeholder {
                color: ${theme.colors.road_grey_600};
            }
        }

        .k-datetimepicker {
            height: 0;
            overflow: hidden;
            width: 0;
        }
    }
`;

function createPopup(popupProps?: PopupProps, onClose?: (event: PopupCloseEvent) => void) {
    return (props) => (
        <Popup
            {...props}
            anchorAlign={{ horizontal: 'left', vertical: 'bottom' }}
            popupAlign={{ horizontal: 'left', vertical: 'top' }}
            {...popupProps}
            className={`${props?.className || ''}  ${popupProps?.className || ''}`}
            onClose={onClose}
        />
    );
}

type DateTimePickerProps = Omit<KendoDateTimePickerProps, 'popup'> & {
    format?: string;
    popupSettings?: PopupProps;
    triggerRef?: RefObject<HTMLElement | undefined>;
};

export function DateTimePicker({
    format = "dd MMM, yy 'at' HH:mm:ss",
    popupSettings,
    triggerRef,
    ...props
}: DateTimePickerProps) {
    const [open, setOpen] = useState(false);
    const ref = useRef<KendoDateTimePicker>();
    const innerTriggerRef = useRef<HTMLElement>();
    triggerRef = triggerRef || innerTriggerRef;

    const intl = useInternationalization();
    const formatParts = (format || '').split(/('[^']*')/gi);

    function handleTriggerClick() {
        setOpen(!open);
    }

    function handleBlur(e) {
        setOpen(false);
    }

    useEffect(() => {
        if (ref.current) {
            (ref.current as any).setShow(open);
        }
    }, [open]);

    const PopupComponent = useMemo(() => {
        return createPopup(popupSettings, () => setOpen(false));
    }, [popupSettings]);

    return (
        <Styles className="app-date-time-picker">
            <AsyncFocusBlur onBlur={handleBlur}>
                {({ onFocus, onBlur }) => (
                    <div onFocus={onFocus} onBlur={onBlur} className="trigger-wrap">
                        <div
                            className={`trigger ${open ? 'trigger-open' : ''}`}
                            ref={triggerRef as any}
                            onClick={handleTriggerClick}
                        >
                            {props.value && (
                                <span className="trigger-text">
                                    {formatParts.map((part, index) => {
                                        part = part.trim();
                                        if (part.startsWith("'")) {
                                            return (
                                                <span className="part-text" key={index}>
                                                    {part.replace(/(^'|'$)/gi, '')}
                                                </span>
                                            );
                                        }
                                        return (
                                            <span className="part-date" key={index}>
                                                {intl.formatDate(props.value as Date, part)}
                                            </span>
                                        );
                                    })}
                                </span>
                            )}

                            {!props.value && (
                                <span className="trigger-placeholder">{props.placeholder || 'Select Date'}</span>
                            )}
                        </div>

                        <KendoDateTimePicker {...props} ref={ref as any} format={format} popup={PopupComponent} />
                    </div>
                )}
            </AsyncFocusBlur>
        </Styles>
    );
}
